/**
 * GetStartedChecklist Component
 *
 * Fixed-position widget that tracks real user onboarding actions.
 * Shows at bottom-right of screen when triggered and not completed.
 *
 * Two visibility states (the legacy "collapsed" header-only state was
 * removed): the panel is either fully visible OR minimized to the top-bar
 * pill. The Minimize2 button sends it to the pill; the X button — which
 * only appears once all required steps are complete — permanently dismisses
 * the checklist in one click (calls `completeChecklist()`, which flips
 * `getStarted.completed`; the panel then hides because `isVisible` reads
 * that flag).
 */

import { useState, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Minimize2 } from 'lucide-react';
import { Button, Progress, cn } from '@scio3/ui';
import { WistiaDialog } from '@/components/shared';
import { useGetStartedChecklist } from '../hooks/useGetStartedChecklist';
import { useGetStartedWidgetStore } from '../stores/get-started-widget-store';
import { GetStartedStep } from './GetStartedStep';
import { GETTING_STARTED_VIDEO_ID } from '../constants';

export function GetStartedChecklist() {
  const { t } = useTranslation();
  const { state, loading, completeChecklist } = useGetStartedChecklist();
  const restorationPending = useGetStartedWidgetStore((s) => s.restorationPending);
  const clearRestoration = useGetStartedWidgetStore((s) => s.clearRestoration);
  const minimizeToPill = useGetStartedWidgetStore((s) => s.minimizeToPill);

  const [activeStepId, setActiveStepId] = useState<string | null>(() => {
    // Open the first incomplete step in display order — for a brand-new
    // account that's "Get Your Screen Set Up". Matches the restore-from-pill
    // logic below, which also picks the first incomplete step regardless of
    // whether it is required.
    const firstIncomplete = state.steps.find((s) => !s.completed);
    return firstIncomplete?.id ?? null;
  });
  const [showVideo, setShowVideo] = useState(false);
  // Brief "fade-out in progress" flag — keeps the panel mounted long enough
  // to play the minimize animation even after the store has flipped to
  // `minimized` (which would otherwise cause an instant unmount). The pill
  // is already mounting and sliding in during this same window — the user
  // perceives a single coordinated handoff from bottom-right to top-bar.
  const [isClosing, setIsClosing] = useState(false);
  // Drives the fade-in keyframe. Toggled true when the user clicks the
  // top-bar pill (see the useLayoutEffect below watching restorationPending)
  // and cleared by onAnimationEnd once the keyframe completes.
  const [isEntering, setIsEntering] = useState(false);

  /**
   * Restore-from-pill choreography. Watches `restorationPending` in the
   * widget store — flipped to `true` by `restoreFromPill()` when the user
   * clicks the top-bar pill. useLayoutEffect (NOT useEffect) so the
   * isEntering update happens BEFORE the browser paints the newly-mounted
   * panel; otherwise the panel paints once at opacity:1, then the keyframe
   * snaps it back to opacity:0 and animates up — a visible flash.
   *
   * AppShell mounts this component unconditionally and the early `return
   * null` keeps the component instance alive across minimize cycles, so a
   * mount-time ref-snapshot would only fire once (at app boot). Watching the
   * store flag directly handles every restore, not just the first.
   *
   *  1. setIsEntering(true)       → fade-in class applied on next render
   *  2. setActiveStepId(null)     → every step starts collapsed (no flicker)
   *  3. clearRestoration()        → consume the signal so the next render is
   *                                 idempotent
   *  4. setTimeout(180ms)         → matches the panel-in keyframe duration;
   *                                 once the fade settles, auto-open the next
   *                                 incomplete step so the user lands directly
   *                                 on what they should work on
   */
  useLayoutEffect(() => {
    if (!restorationPending) return;
    setIsEntering(true);
    setActiveStepId(null);
    clearRestoration();
    // Fire-and-forget — NO cleanup return on this timeout. `clearRestoration`
    // flips `restorationPending` back to false, which re-runs this effect;
    // React runs the previous cleanup first, which (if we returned one) would
    // clearTimeout BEFORE the 180ms elapses, and the auto-open would never
    // fire. The early-return on the re-run already prevents duplicate timers.
    setTimeout(() => {
      const nextIncomplete = state.steps.find((s) => !s.completed);
      setActiveStepId(nextIncomplete?.id ?? null);
    }, 180);
    // state.steps intentionally omitted — capturing the steps at the moment
    // of restoration is correct (the user can't complete a step in <180ms).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restorationPending, clearRestoration]);

  // Render the panel while visible OR while still animating out. Once the
  // fade-out finishes, isClosing resets and the next render unmounts it.
  if (!state.isVisible && !isClosing) {
    return null;
  }

  const handleDismiss = async () => {
    // Permanently dismiss the checklist. Backend writes `getStarted.completed
    // = true` so this never re-appears for the user. No top-bar pill, no
    // re-entry — the ? help dropdown is the long-term home for resources now.
    await completeChecklist();
  };

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Flip the store FIRST so the top-bar pill mounts and starts its
    // slide-in animation immediately — the two transitions overlap rather
    // than playing back-to-back. The panel stays rendered via `isClosing`
    // until its own fade-out animation completes.
    //
    // `minimizeToPill` (instead of `setWidgetState('minimized')`) ALSO sets
    // the `justMinimized` transient flag so the pill knows this is a fresh
    // user action and should play its slide-in animation. On a page reload
    // with widgetState already restored from localStorage as 'minimized',
    // the flag is false → pill renders statically with no spurious animation.
    setIsClosing(true);
    minimizeToPill();
  };

  const toggleStep = (stepId: string) => {
    setActiveStepId((current) => (current === stepId ? null : stepId));
  };

  // X (Dismiss guide) only becomes available once every required step is
  // done — until then, the only escape is the Minimize2 button (send to pill).
  // This is intentional: we don't want users to permanently kill the guide
  // before they've actually seen the steps through.
  const canDismiss = state.isComplete;

  return (
    <div
      className={cn(
        'fixed bottom-24 right-6 z-40 w-80',
        isClosing && 'animate-get-started-panel-out pointer-events-none',
        isEntering && 'animate-get-started-panel-in',
      )}
      data-testid="get-started-checklist"
      onAnimationEnd={(e) => {
        // Filter by name so we only react to the wrapper's own animations,
        // never the inner step's accordion animations bubbling up.
        if (e.animationName === 'get-started-panel-out') {
          setIsClosing(false);
        } else if (e.animationName === 'get-started-panel-in') {
          setIsEntering(false);
        }
      }}
    >
      {/* Outer container — gray frame around the white body card. */}
      <div className="rounded-lg bg-muted pt-1 shadow-[0_0_8px_1px_rgba(0,0,0,0.12)] dark:bg-panel-hover dark:shadow-none">
        {/* Header — sits directly on bg, no card. */}
        <div className="flex min-h-10 items-center gap-2 px-2 pb-1 pt-1">
          <span className="shrink-0 pl-1 text-sm font-semibold">{t('getStarted.title')}</span>
          {/* Progress bar — primary visual element in header */}
          <Progress value={state.progressPercent} className="h-2 flex-1 bg-muted-foreground/20" />
          {/* Right-side action cluster — Minimize2 + X grouped with no gap so
              the two icon buttons read as a single pair, separated from
              the progress bar by the parent's gap-2. Minimize2 sends the
              widget to the top-bar pill (always available); X (Dismiss
              guide) only appears once all required steps are complete. */}
          <div className="flex shrink-0 items-center">
            <Button
              data-testid="checklist-minimize-btn"
              variant="ghost"
              size="icon-sm"
              className="shrink-0 text-muted-foreground hover:text-foreground"
              onClick={handleMinimize}
              aria-label={t('getStarted.minimize')}
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            {canDismiss && (
              <Button
                data-testid="checklist-dismiss-btn"
                variant="ghost"
                size="icon-sm"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => void handleDismiss()}
                disabled={loading}
                aria-label={t('Dismiss guide')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Body — white card with border, steps + need help. */}
        <div className="rounded-lg border border-border bg-card p-3 dark:bg-muted">
          {/* Steps list */}
          <div className="space-y-1">
            {state.steps.map((step) => (
              <GetStartedStep
                key={step.id}
                step={step}
                isActive={activeStepId === step.id}
                onToggle={() => toggleStep(step.id)}
              />
            ))}
          </div>

          {/* Need help section */}
          <div className="mt-3 flex flex-wrap items-center gap-1 px-2 text-xs select-none">
            <span className="text-muted-foreground">{t('getStarted.needHelp')}</span>
            <Button
              data-testid="checklist-watch-video-btn"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs underline"
              onClick={() => setShowVideo(true)}
            >
              {t('getStarted.watchVideo')}
            </Button>
          </div>
        </div>
      </div>

      <WistiaDialog
        videoId={showVideo ? GETTING_STARTED_VIDEO_ID : null}
        onClose={() => setShowVideo(false)}
      />
    </div>
  );
}
